import { parse as yamlParse } from 'yaml';
import { getJsonSchemaErrorString } from '../../../components/jsonSchemaValidator';
import schemaDashboardTemplate from '../../../json-schemas/schemaDashboardTemplate';
import { getUserError } from '../../../components/miscUtils';
import {
  dashboardTemplateContentSchema,
  TpSectionDTO,
  TpTopbarElementDTO,
  TpDashboardTemplateContentDTO,
} from '../../../data/dashboard-template/schemas';
import {
  DASHBOARDELEMIDS,
  TpDashboardCreationContext,
  TpDashboardDefinition,
  TpDashboardSectionDefinition,
  TpDashboardTopbarDefinition,
} from '../dashboard/interfaces';
import { generateElementsForSection } from './dashboardsSlice';
import type { TpTemplateInfo } from '../../../api/models/assets';

/**
 * Create a dashboard definition from a given dashboard template schema.
 */
export function createDashboardFromTemplateSchema(
  ctx: TpDashboardCreationContext,
  id: string,
  dashboardSchema: TpDashboardTemplateContentDTO,
  templateInfo: TpTemplateInfo,
): TpDashboardDefinition {
  const sectionsToRender = dashboardSchema.elements.sections.filter((section: TpSectionDTO) => {
    if (section.requiredTableId) return ctx.dataContext.dataTables.map((dt) => dt.id).includes(section.requiredTableId);
    return true;
  });
  const collectedSections: TpDashboardSectionDefinition[] = sectionsToRender.map((section: TpSectionDTO) => {
    return {
      elemType: DASHBOARDELEMIDS.Section,
      id: section.id,
      title: section.title ?? 'Section',
      description: section.description,
      requiredTableId: section.requiredTableId,
      collapsed: section.collapsed || false,
      elements: generateElementsForSection(
        ctx,
        section,
        dashboardSchema.elements.widgets,
        dashboardSchema.elements.actions || {},
      ),
    };
  });

  let topbarTables: TpDashboardTopbarDefinition | undefined = undefined;
  if (dashboardSchema.elements.topbar) {
    topbarTables = {
      elemType: DASHBOARDELEMIDS.TopBarTables,
      tableSelection: dashboardSchema.elements.topbar.map((topbar: TpTopbarElementDTO) => {
        return topbar.id;
      }),
    };
  }

  return {
    id,
    elemType: DASHBOARDELEMIDS.Dashboard,
    name: dashboardSchema.displayName ?? '',
    elements: collectedSections,
    dashboardActionDefs: dashboardSchema.elements.actions || {},
    widgetGlobalConfig: dashboardSchema.elements.widgetGlobalConfig || {},
    topbarTables,
    templateInfo: { ...templateInfo },
  };
}

export function createDashboardFromTemplate(
  ctx: TpDashboardCreationContext,
  templateInfo: TpTemplateInfo,
  id: string,
): TpDashboardDefinition {
  const yamlString = yamlParse(templateInfo.yamlString);

  // AJV Dashboard schema & rules
  const errorString = getJsonSchemaErrorString(schemaDashboardTemplate, yamlString.templateContent);
  if (errorString) throw getUserError('Dashboard template error:\n\n' + errorString);

  // Zod Dashboard schema & rules
  const result = dashboardTemplateContentSchema.safeParse(yamlString.templateContent);
  if (!result.success) {
    // Get a human-readable error string from the ZOD error
    const zodErrorString = result.error;
    throw getUserError('Dashboard template error:\n\n' + zodErrorString);
  }

  const validDash = result.data;

  return createDashboardFromTemplateSchema(ctx, id, validDash, templateInfo);
}
